import { dataPullLock, getReplicates } from "@/data/ReplicateEffect.ts";
import { collectionsMap } from "@/data/CollectionsMap.ts";
import delay from "delay";
import { deviceSettingLock } from "@/data/DeviceSettingHub.ts";
import { toast } from "react-toastify";
import { deepSignal, signal, useSignal } from "@/react/core/reactive.ts";
import { setDeviceAsMaster } from "@/react/MasterMachineView/MasterMachineView.tsx";
import { DATA_INITED_2 } from '@/data/data-const'
import { firstTimeConnect } from "@/data/ReplicateState.ts";
import SurrealClient from "@/shared/SurrealClient.ts";
import { getDeviceId } from '@/shared/getDeviceId'
import dialogService from "@/react/SystemService/dialogService.tsx";
import axios from "axios";
import { getApiUrl } from "@/shared/utils.ts";
import msgBox from "@/react/SystemService/msgBox.tsx";
import _ from "lodash";
import { posSetting0, posSettingLock } from "@/data/PosSettingsSignal.ts";
import { server } from "@/data/Server.ts";
import { posSync0, posSyncLock } from "@/data/PosSyncState.ts";
import semver from "semver";
import { deviceSetting0 } from "@/data/DeviceSettingSignal.ts";
import { zipTextToBlob } from "@/shared/zipUtils.ts";
import { uploadFile } from "@/shared/FileUploader.ts";
import { sendEmail } from "@/shared/EmailSender.ts";
import dayjs from "dayjs";
import { srmLogic } from "@/srm/logic"
import { connectFlow, userFLow } from "@/shared/logger.ts";
import { loginUser } from "@/data/UserSignal.ts";
import { setIsMaster } from "@/lib/master-signal.ts";
import Dexie from "dexie";
import { getUrlParam } from "@/shared/utils2.ts";
export const DISCONNECT_PASSWORD = "999"

export enum ModeConnect {
  DEFAULT = 1,
  PAIRING_CODE = 2,
  DEVELOPER = 3,
}
const initialMode: ModeConnect = (import.meta.env.MODE === 'production') ? ModeConnect.DEFAULT : ModeConnect.DEVELOPER;
export const [isChangeBg, setIsChangeBg] = signal<boolean>(!!localStorage.getItem('isDisconnectStore'))
export const [posDatabase, setPosDatabase] = signal<string>("");
export const [passcode, setPasscode] = signal<string>("")
export const [pairingCode, setPairingCode] = signal<string>("")
export const [currentMode, setCurrentMode] = signal<ModeConnect>(initialMode)
export const [isDisabledConnect, setIsDisabledConnect] = signal<boolean>(false)

export function setReplicateServerUserNamePassword(storeId: number, password: string) {
  if (posSync0() && !posSync0()?.replicateServer) {
    posSync0()!.replicateServer = deepSignal({} as {
      [dbName: string]: string
    })
  }
  _.assign(posSync0()?.replicateServer, { username: `user_n${storeId}`, password })
}

export async function clearIndexedDb() {
  for (const dbName of ['pos', 'pos2']) {
    const db = new Dexie(dbName)
    await db.open()
    await db.delete();
    db.close();
  }
  localStorage.removeItem('rx-storage-localstorage-pos--_rxdb_internal--0');
  localStorage.removeItem('rx-storage-localstorage-changestream-pos--_rxdb_internal--0');
}

export async function getTokenIfNotExists(_passcode: string = passcode(), storeId: string = posDatabase()) {
  let toastId
  if (_.isEmpty(posSync0()?.replicateServer)) {
    toastId = toast.info('Try to request token.', { autoClose: false })
    const passcodeFromParam = getUrlParam('connect-code')
    if (!_passcode) {
      if (!passcodeFromParam) {
        toast.done(toastId)
        return
      }
      _passcode = passcodeFromParam!
    }
  } else return true
  const response = await axios.post(`${getApiUrl()}/api/authDevice`, {
    passcode: _passcode,
    mode: 'passcode',
    storeId: storeId || '1',
    deviceId: getDeviceId()
  })
  await setReplicateTokenForDb('cloud', response.data.token)
  await setReplicateTokenForDb('refreshToken', response.data.refreshToken)
  setReplicateServerUserNamePassword(storeId, _passcode)
  toast.done(toastId)
  return true
}

export async function setReplicateTokenForDb(db: string, token: string) {
  if (posSync0() && !posSync0()?.replicateServer) {
    posSync0()!.replicateServer = deepSignal({} as {
      [dbName: string]: string
    })
  }
  _.assign(posSync0()?.replicateServer, { [db]: token })
}

export function replicateFactory() {
  async function waitForReplicate () {
    // const closeLoadingDialog = dialogService.progress({ title: "Loading data" });
    const t = toast("Loading data..., please wait !!!", {
      autoClose: false,
      progress: 100,
    })
    while (Object.keys(getReplicates()).length < collectionsMap('sync').length) {
      await delay(1000)
    }
    for (const { collection, name } of collectionsMap('sync')) {
      await getReplicates()[name]?.awaitInSync()
    }
    toast.done(t)
    // closeLoadingDialog()
  }

  async function connectAsDevelopver () {
    await waitForReplicate()
    localStorage.setItem("change-master-device", "true");
    await onDisconnect();
    await srmLogic.resetSrmSettingsToDefault()
  }

  async function connectAsMaster () {
    await waitForReplicate()
    localStorage.setItem('clear-print-cache', 'true');
    await posSettingLock.acquireAsync()
    await deviceSettingLock.acquireAsync()
    if (deviceSetting0()) {
      await setDeviceAsMaster(deviceSetting0()!)
    }
    await dataPullLock.release()
  }

  async function connectAsClient () {
    await waitForReplicate()
    await dataPullLock.release()
    // clearPrintCache();
  }

  return {
    connectAsDevelopver,
    connectAsMaster,
    connectAsClient
  }
}

export const backupCollections = async (email: string) => {
  if (!email) return toast.error('Please enter email!')
  if (!validateEmail(email)) return toast.error('Email invalid!')
  const t = toast.info('Processing, please wait!', { autoClose: false })
  const collections = collectionsMap('backup')
  const collectionsData = [];
  const csvFiles = [];

  for (const item of collections) {
    const data = await item.collection.find().exec();
    if (data.length > 0)
      collectionsData.push({ data: data.map(d => d.toJSON()), collectionName: item.name });
  }

  for (const { data, collectionName } of collectionsData) {
    const jsonData = JSON.stringify(data, null, 4);
    csvFiles.push([`${collectionName}.json`, jsonData]);
  }

  const blob = await zipTextToBlob(...csvFiles);
  const blobUrl = await uploadFile('srmBackup', blob, progress => console.log('progress', progress))
  console.log('💾 Data uploaded', blobUrl)
  await sendEmail(email, `Exported data ${dayjs().format()}`, `Your backup data is: ${blobUrl}`)
  toast.done(t)
  toast.success('Backup success, please check your email!')
}

export function validateEmail(email: string): boolean {
  const regex = /^[\p{L}\p{N}._%+-]+@[\p{L}\p{N}.-]+\.\p{L}+$/u;
  return regex.test(email);
}

export async function onDisconnect(reload?: boolean) {
  localStorage.removeItem("init-device-setting");
  localStorage.setItem('isDisconnectStore', 'true')
  setIsChangeBg(true)
  const ds = deviceSetting0()
  if (ds && ds.srm) {
    const srm = _.cloneDeep(ds.srm)
    await ds.doc.remove()
    await ds.doc.incrementalUpdate({ $set: { srm } })
  }
  await delay(1000)
  posSync0().id = undefined;
  localStorage.removeItem('storeId')
  posSync0().replicationIdentifierPrefix = posSync0().replicationIdentifierPrefix ? posSync0().replicationIdentifierPrefix! + 1 : 1;
  toast("Disconnected to the store", { type: "info" });
  await delay(2000)
  if (!reload) {
    location.reload();
  }
}

export async function onConnect(mode: 'master' | 'client' | 'support' , store: string, refreshToken: string, isReconnect?: boolean) {
  connectFlow(`connect as ${mode} to store: ${store}`)
  const closeRegisterDialog = dialogService.progress({ title: 'Connect' })
  await posSettingLock.acquireAsync();
  const deviceId = getDeviceId()
  localStorage.setItem(DATA_INITED_2, 'true');
  if (!store) {
    closeRegisterDialog()
    toast.error("You have not entered Database ID")
    return
  }
  if (!deviceSetting0()) {
    closeRegisterDialog()
    toast.error("Something went wrong")
    return
  }
  const db = await SurrealClient.getSurrealClient('cloud')
  if (!db) {
    toast.error("Something went wrong")
    closeRegisterDialog()
    return
  }
  setIsDisabledConnect(true)

  const [existedDevice] = await db.query(`select * from only Device:${deviceId}`)
  console.log('device', existedDevice);
  try {
    const dbTokenRes = await axios.post(
      `${getApiUrl()}/api/getDbToken`, {
        refreshToken,
        storeId: store,
        deviceId
      }
    )
    const token = dbTokenRes.data?.token;
    await setReplicateTokenForDb(`n${store}`, token);
  } catch (e: any) {
    setIsDisabledConnect(false)
    closeRegisterDialog()
    await msgBox.show('Error', 'Failed to connect database: ' + e.message, msgBox.Buttons.OK, msgBox.Icons.Error)
    return;
  }
  if (!existedDevice && !isReconnect && mode !== 'support') {
    try {
      const deviceRes = await axios.post(
        `${getApiUrl()}/api/registerDevice`,
        { store: parseInt(store), deviceId: deviceId, name: deviceSetting0()?.name, show: true }
      )
      deviceSetting0()!.cloudRegister = true
      closeRegisterDialog()
    } catch (e: any) {
      setIsDisabledConnect(false)
      closeRegisterDialog()
      await msgBox.show('Error', 'Failed to connect database: ' + e.message, msgBox.Buttons.OK, msgBox.Icons.Error)
    }
  } else {
    console.log('existedDevice')
    deviceSetting0()!.cloudRegister = true
  }
  firstTimeConnect.v = true;
  setIsMaster(false);
  localStorage.setItem("posDatabase", store);
  localStorage.setItem("tempServer", server()!);
  // localStorage.setItem("needInitStoreStep", "1");
  localStorage.setItem("connectAsMaster", mode === 'master' ? 'true': 'false');

  await clearIndexedDb();
  //trigger shouldInitStore
  localStorage.setItem("needInitStoreStep", "2");

  posSync0()!.id = undefined;
  localStorage.removeItem('storeId')
  await delay(100);
  setIsDisabledConnect(false)
  setTimeout(() => {
    closeRegisterDialog();
    location.reload();
  }, 500);
  return;

}

export async function onReconnect() {
  userFLow("Passcode reconnect", {
    username: loginUser()?.name
  })
  await posSyncLock.acquireAsync()
  const closeRegisterDialog = dialogService.progress({ title: 'Reconnect' })
  try {
    localStorage.setItem('modeConnect', '1')
    const storeId = posSync0().id
    posSync0().replicationIdentifierPrefix = posSync0().replicationIdentifierPrefix ? posSync0().replicationIdentifierPrefix! + 1 : 1;
    posSync0().id = undefined;
    localStorage.removeItem('storeId')
    const isMaster = deviceSetting0()?._id === posSetting0()?.masterDevice
    await onConnect(isMaster ? 'master' : 'client', storeId!.toString(), posSync0()?.replicateServer?.refreshToken!)
  } catch (e) {
    console.log(e)
    toast.error('Failed to reconnect')
  } finally {
    closeRegisterDialog();
  }
}

export async function onForceUpdate() {
  toast.info('Updating...')
  try {
    const db = await SurrealClient.getSurrealClient('cloud')
    if (!db || !posSync0()?.id) return toast('Failed to update version, need connect to store first!')
    const [latestV] = await db.query<Array<any>>('SELECT * FROM app_releases ORDER BY releaseDate DESC LIMIT 1')
    const [currentV] = await db.query<Array<any>>(`SELECT * FROM StoreVersion:${posSync0().id}`)
    if (!latestV[0]?.version || !currentV[0]?.version) return toast('Failed to update version')
    if (semver.gt(latestV[0].version, currentV[0].version)) {
      await db.query(`UPDATE StoreVersion:${posSync0().id} SET version = '${latestV[0].version}'`)
      toast.success(`Update success, new version is ${latestV[0].version}`)
      await db.query(`INSERT INTO Request CONTENT { "storeId" = ${posSync0().id}, "command": "update" }`)
      return
    }
    toast.success('This version is up to date')
  } catch (err) {
    toast.error('Failed to update version ' + err)
  }
}